import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users } from "lucide-react";

/**
 * TeamSummaryChips — Feb 24, 2026
 * Compact 7-slot team-summary strip used on every project card / list row.
 * Reads from `GET /api/projects/{project_id}/team` (single) or accepts
 * pre-fetched slots via the `slots` prop (batch, preferred on list pages).
 *
 * Props:
 *   projectId — design_project id.
 *   slots     — optional pre-fetched slot array (from useProjectTeams).
 *               When provided, the component skips the individual fetch.
 *   compact   — single-line horizontal strip. Default: true.
 */
export default function TeamSummaryChips({ projectId, slots: preSlots, compact = true }) {
  const [slots, setSlots] = useState(preSlots || null);

  useEffect(() => {
    if (preSlots !== undefined) { setSlots(preSlots); return; }
    if (!projectId) return;
    let cancelled = false;
    api.get(`/projects/${projectId}/team`)
      .then(({ data }) => { if (!cancelled) setSlots(data.slots || []); })
      .catch(() => { /* 403 or 404 → hide silently */ });
    return () => { cancelled = true; };
  }, [projectId, preSlots]);

  if (!slots) return null;

  return (
    <div
      className={compact
        ? "flex items-center gap-1 flex-wrap"
        : "grid grid-cols-2 md:grid-cols-4 gap-2"}
      data-testid={`team-chips-${projectId}`}
    >
      {compact && (
        <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1 mr-1">
          <Users className="w-3 h-3 text-[#d4af37]"/>Team
        </span>
      )}
      {slots.map((s) => <TeamChip key={s.key} slot={s} compact={compact} />)}
    </div>
  );
}

function TeamChip({ slot, compact }) {
  const assigned = !!slot.user_name;
  const initials = assigned
    ? slot.user_name.split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase()
    : "—";
  const tone = assigned
    ? "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5"
    : "border-[#262626] text-gray-500 bg-[#0a0a0a]";
  const tooltip = assigned
    ? `${slot.label} · ${slot.user_name} · ${slot.user_email || "no email"}`
    : `${slot.label} · Not Assigned`;
  const testid = `team-chip-${slot.key}${assigned ? "" : "-empty"}`;

  if (compact) {
    return (
      <span
        title={tooltip}
        className={`inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest ${tone}`}
        data-testid={testid}
      >
        <span className="opacity-70">{slot.label.split(" ").map(w => w[0]).join("")}</span>
        <span className="font-semibold">{initials}</span>
      </span>
    );
  }
  return (
    <div className={`border rounded p-2 ${tone}`} data-testid={testid}>
      <p className="text-[9px] font-mono uppercase tracking-widest opacity-70">{slot.label}</p>
      {assigned ? (
        <>
          <p className="text-sm mt-0.5 truncate text-white">{slot.user_name}</p>
          <p className="text-[10px] text-gray-500 truncate">{slot.user_email}</p>
        </>
      ) : (
        <p className="text-sm italic text-gray-500 mt-0.5">Not Assigned</p>
      )}
    </div>
  );
}

/**
 * useProjectTeams — batch resolver hook.
 * Fetches team slots for many project ids in one call so list dashboards
 * don't fire N individual requests. Returns `{[projectId]: slots}`.
 */
export function useProjectTeams(projectIds) {
  const [map, setMap] = useState({});
  useEffect(() => {
    const ids = Array.from(new Set((projectIds || []).filter(Boolean)));
    if (ids.length === 0) { setMap({}); return; }
    let cancelled = false;
    api.post("/projects/teams/batch", { project_ids: ids })
      .then(({ data }) => { if (!cancelled) setMap(data.teams || {}); })
      .catch(() => { /* leave map empty; chips will hide */ });
    return () => { cancelled = true; };
  // Stable string key of the sorted ids avoids re-firing on referential
  // identity changes when the parent list is memoized to the same content.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ (projectIds || []).slice().sort().join(",") ]);
  return map;
}
